import React, {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementType,
  forwardRef,
  type ReactElement,
  Ref,
} from 'react';
import { Link as MuiLink } from '@mui/material';

type HTMLTagMap = {
  div: HTMLDivElement;
  span: HTMLSpanElement;
  a: HTMLAnchorElement;
  button: HTMLButtonElement;
};

type MuiProps = ComponentProps<typeof MuiLink>;

type PolymorphicRef<C extends ElementType> = C extends keyof HTMLTagMap ? Ref<HTMLTagMap[C]> : Ref<unknown>;

export type Props<C extends ElementType> = {
  component?: C;
  rows?: number;
  fontWeight?: MuiProps['fontWeight'];
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase';
} & MuiProps &
  Omit<ComponentPropsWithoutRef<C>, keyof MuiProps | 'component'>;

function PolymorphicComponentImpl<C extends ElementType = 'a'>(props: Props<C>, ref: PolymorphicRef<C>): ReactElement {
  const { rows, fontWeight, textTransform, sx, ...rest } = props;

  return (
    <MuiLink
      {...rest}
      // TypeScript can't infer polymorphic ref on MUI base, safe to cast
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as Ref<any>}
      sx={{
        ...(fontWeight && { fontWeight }),
        ...(textTransform && { textTransform }),
        ...(typeof rows === 'number' &&
          rows > 1 && {
            position: 'relative',
            display: '-webkit-box',
            overflow: 'hidden',
            overflowWrap: 'anywhere',
            textAlign: 'initial',
            textOverflow: 'ellipsis',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: `${rows}`,
          }),
        ...sx,
      }}
    />
  );
}

const PolymorphicComponent = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  PolymorphicComponentImpl as unknown as (props: Props<any>, ref: PolymorphicRef<any>) => ReactElement,
);

const Link = PolymorphicComponent as <C extends ElementType = 'a'>(
  props: Props<C> & { ref?: PolymorphicRef<C> },
) => ReactElement | null;

// @ts-expect-error TS can't detect displayName from forwardRef
Link.displayName = 'Link';

export default Link;
